import React from 'react';

import { classnames, useClassnames, visuallyHidden } from '@lumapps/classnames';
import { GenericEntityBlock } from '@lumapps/lumx-blocks/components/GenericEntityBlock';
import { type ThumbnailWithIconProps } from '@lumapps/lumx-images/components/ThumbnailWithIcon';
import { Size, Text } from '@lumapps/lumx/react';
import type { DocumentV1 } from '@lumapps/medias-document/types';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';

import { MEDIAS } from '../../keys';
import { FILE_TYPES_ICONS, getMediaContent, getMediaIcon } from '../../utils';
import { resizeImage } from '../../utils/resizeImage';

export interface DocumentBlockProps {
    /** The document object (`DocumentV1` type) to display. */
    document: DocumentV1;
    /** Whether the thumbnail should resize */
    shouldResize?: boolean;
}

export const CLASSNAME = 'document-block';

/**
 * Displays a given document as a block
 *
 * @family Blocks
 * @param {DocumentBlockProps}
 * @returns DocumentBlock
 */
export const DocumentBlock: React.FC<DocumentBlockProps> = ({ document, shouldResize = true }) => {
    const lang = useSelector(currentLanguageSelector);

    const { block } = useClassnames(CLASSNAME);
    const { translateObject, translateKey } = useTranslate();

    const { thumbnail, name, isFolder } = document;

    const fileName = translateObject(name);

    const thumbnailOrIcon: ThumbnailWithIconProps = { size: Size.m };

    // Display thumbnail
    const translatedThumbnail = translateObject(thumbnail);
    if (translatedThumbnail) {
        thumbnailOrIcon.image = shouldResize ? resizeImage(translatedThumbnail, 80) : translatedThumbnail;
    }

    // Or display icon
    if (!translatedThumbnail) {
        const mediaContent = getMediaContent(document, translateObject, lang);
        const mediaIcon = isFolder ? FILE_TYPES_ICONS.folder : getMediaIcon(mediaContent);
        thumbnailOrIcon.icon = mediaIcon.icon;
        thumbnailOrIcon.color = mediaIcon.color;
    }

    return (
        <GenericEntityBlock
            className={block()}
            // The thumbnail is passed as a prop, as seen in the original code.
            thumbnail={thumbnailOrIcon}
            // Props like `shouldCenterTitle` will still work.
            shouldCenterTitle
            title={
                <>
                    {isFolder && (
                        /*
                         * Accessibility: Add a visually hidden prefix for screen readers
                         * to clarify that folders are navigable items.
                         */
                        <Text as="span" className={classnames(visuallyHidden())}>
                            {translateKey(MEDIAS.OPEN_FOLDER)}
                        </Text>
                    )}

                    {fileName}
                </>
            }
        />
    );
};
